import {
  checklistItemSchema,
  checklistSectionSchema,
  type ChecklistItem,
  type ChecklistSection,
} from '@growup/streaming-shared';
import { Button, Card, Checkbox, Field, Input, Notice, Select, StatusBadge } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { MARKED_BY_LABEL, SECTION_LABEL } from './labels';

const PATH = '/api/streaming/checklist/items';
const listSchema = z.array(checklistItemSchema);

/**
 * A lista da checklist dos 30 dias (GS4, RN-GS7), for the Stream Staff: text,
 * order, required and who ticks each item. Automatic items keep their rule
 * and can only be switched on or off. Closed checklists keep what they had.
 */
export function ChecklistItemsView() {
  const [items, setItems] = useState<ChecklistItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson(PATH, listSchema)
      .then(setItems)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const save = async (run: () => Promise<unknown>) => {
    setError(null);
    try {
      setItems(listSchema.parse(await run()));
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-gu-ink-muted">
        A checklist dos primeiros 30 dias de cada streamer. Os itens automáticos marcam-se sozinhos
        (Twitch ligada, mural, padrinho, conversa de entrada, transmissões e horas).
      </p>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {checklistSectionSchema.options.map((section) => {
        const rows = items?.filter((i) => i.section === section) ?? [];
        return (
          <Card key={section} headingLevel={2} title={SECTION_LABEL[section]}>
            <ul className="flex flex-col divide-y divide-gu-line">
              {rows.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  onSave={(patch) => save(() => send('PUT', `${PATH}/${item.id}`, patch))}
                />
              ))}
            </ul>
            <NewItem section={section} onAdd={(body) => save(() => send('POST', PATH, body))} />
          </Card>
        );
      })}
    </div>
  );
}

function ItemRow({
  item,
  onSave,
}: {
  item: ChecklistItem;
  onSave: (patch: Record<string, unknown>) => Promise<boolean>;
}) {
  const [title, setTitle] = useState(item.title);
  const [help, setHelp] = useState(item.help);
  const [required, setRequired] = useState(item.required);
  const [markedBy, setMarkedBy] = useState(item.markedBy);
  const [active, setActive] = useState(item.active);
  const auto = item.rule !== null;
  const changed =
    title !== item.title ||
    help !== item.help ||
    required !== item.required ||
    markedBy !== item.markedBy ||
    active !== item.active;

  return (
    <li className="flex flex-col gap-2 py-3" aria-label={`Item ${item.title}`}>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Item" className="min-w-64 flex-1">
          <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        {auto ? (
          <span className="mb-2">
            <StatusBadge tone="info">Automático</StatusBadge>
          </span>
        ) : (
          <Field label="Quem marca" className="w-40">
            <Select
              value={markedBy}
              onChange={(e) => setMarkedBy(e.target.value as 'streamer' | 'padrinho')}
            >
              <option value="streamer">{MARKED_BY_LABEL.streamer}</option>
              <option value="padrinho">{MARKED_BY_LABEL.padrinho}</option>
            </Select>
          </Field>
        )}
      </div>
      <Field label="Ajuda" optional>
        <Input value={help} maxLength={500} onChange={(e) => setHelp(e.target.value)} />
      </Field>
      <div className="flex flex-wrap items-center gap-4">
        <Checkbox
          label="Obrigatório"
          checked={required}
          onChange={(e) => setRequired(e.target.checked)}
        />
        <Checkbox label="Ativo" checked={active} onChange={(e) => setActive(e.target.checked)} />
        <Button
          size="sm"
          variant="secondary"
          disabled={!changed || title.trim().length < 3}
          onClick={() =>
            void onSave({
              title,
              help,
              required,
              active,
              ...(auto ? {} : { markedBy }),
            })
          }
        >
          Guardar
        </Button>
      </div>
    </li>
  );
}

function NewItem({
  section,
  onAdd,
}: {
  section: ChecklistSection;
  onAdd: (body: unknown) => Promise<boolean>;
}) {
  const [title, setTitle] = useState('');
  const [markedBy, setMarkedBy] = useState<'streamer' | 'padrinho'>('padrinho');
  const [required, setRequired] = useState(true);

  return (
    <div className="flex flex-wrap items-end gap-2 border-t border-gu-line pt-3">
      <Field label={`Novo item em ${SECTION_LABEL[section]}`} className="min-w-64 flex-1">
        <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="Quem marca" className="w-40">
        <Select
          aria-label={`Quem marca o novo item em ${SECTION_LABEL[section]}`}
          value={markedBy}
          onChange={(e) => setMarkedBy(e.target.value as 'streamer' | 'padrinho')}
        >
          <option value="streamer">{MARKED_BY_LABEL.streamer}</option>
          <option value="padrinho">{MARKED_BY_LABEL.padrinho}</option>
        </Select>
      </Field>
      <Checkbox
        label="Obrigatório"
        checked={required}
        onChange={(e) => setRequired(e.target.checked)}
      />
      <Button
        size="sm"
        disabled={title.trim().length < 3}
        onClick={() =>
          void onAdd({ section, title, markedBy, required }).then((ok) => ok && setTitle(''))
        }
      >
        Acrescentar
      </Button>
    </div>
  );
}
